/**
 * The entity surface a sandboxed Page may open or create in the host app, and
 * the prefill fields each one accepts.
 *
 * This module is the RUNTIME source of truth for `useNavigation().openEntity()`:
 * the SDK types its `entity` argument from it and the host bridge validates
 * every payload against it before anything reaches a modal or drawer. It is
 * deliberately dependency-free so both the iframe bundle and the bridge's
 * dependency-free unit test can import it.
 *
 * Why an allowlist rather than passing the payload through: the payload crosses
 * postMessage from a sandboxed iframe and ends up as props on `openDrawer` /
 * `$upModal.open` / `openModal`, which are loosely typed and feed save paths.
 * Only declared keys survive, and every value is type- and length-checked.
 * Functions can't cross postMessage, so the risk this closes is prop injection,
 * not callback injection.
 *
 * The DECLARED surface a Page's code is type-checked against lives in
 * `base/src/core/services/ai/pages/sdkTypes.ts` — `ui` doesn't import
 * `upsales-base`, which is why the two are separate files (the same reason
 * `PageEditorEntity` is already declared twice today). They must be edited
 * together; anything declared there but missing here is reported by
 * `pageEntityActions` as a developer error rather than silently dropped.
 */

/** How a prefill value is validated. Nothing else is accepted. */
type FieldType = 'id' | 'text' | 'longText' | 'orderType';

const TEXT_MAX = 200;
const LONG_TEXT_MAX = 20_000;

type EntitySpec = {
	/** False for entities that have no create form reachable from a Page. */
	create: boolean;
	/** False for `mail`, which is a composer rather than a record with an id. */
	open: boolean;
	fields: Readonly<Record<string, FieldType>>;
};

/**
 * `clientId` / `contactId` are the only relation refs a Page may pass. The host
 * hydrates them into whatever shape each editor wants (some need `{id, name}`,
 * some take a bare id), so a Page never has to know the difference.
 */
export const PAGE_ENTITY_SPECS: Readonly<Record<string, EntitySpec>> = {
	client: { create: false, open: true, fields: {} },
	contact: {
		create: true,
		open: true,
		fields: { clientId: 'id', firstName: 'text', lastName: 'text', email: 'text', phone: 'text', title: 'text' }
	},
	order: {
		create: true,
		open: true,
		fields: {
			clientId: 'id',
			contactId: 'id',
			description: 'text',
			notes: 'longText',
			type: 'orderType'
		}
	},
	agreement: { create: true, open: true, fields: { clientId: 'id' } },
	activity: { create: true, open: true, fields: { clientId: 'id', contactId: 'id', description: 'text' } },
	todo: { create: true, open: true, fields: { clientId: 'id', contactId: 'id', description: 'text' } },
	phonecall: { create: true, open: true, fields: { clientId: 'id', contactId: 'id', description: 'text' } },
	appointment: { create: true, open: true, fields: { clientId: 'id', contactId: 'id', description: 'text' } },
	ticket: { create: true, open: true, fields: { clientId: 'id', contactId: 'id', description: 'text' } },
	mail: {
		create: true,
		open: false,
		fields: { clientId: 'id', contactId: 'id', subject: 'text', body: 'longText' }
	},
	campaign: { create: false, open: true, fields: {} },
	segment: { create: false, open: true, fields: {} },
	lead: { create: false, open: true, fields: {} },
	projectplan: { create: false, open: true, fields: {} }
} as const;

export const PAGE_ENTITIES = Object.keys(PAGE_ENTITY_SPECS) as ReadonlyArray<keyof typeof PAGE_ENTITY_SPECS>;
export type PageEntity = keyof typeof PAGE_ENTITY_SPECS;

/** What a Page passes to `openEntity`. `id` opens that record; without it the payload prefills a create form. */
export type PageEntityPayload = { id?: number } & Record<string, string | number | undefined>;

export const isPageEntity = (value: unknown): value is PageEntity =>
	typeof value === 'string' && Object.prototype.hasOwnProperty.call(PAGE_ENTITY_SPECS, value);

const isPositiveInt = (value: unknown): value is number =>
	typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const cleanValue = (type: FieldType, value: unknown): string | number | undefined => {
	switch (type) {
		case 'id':
			return isPositiveInt(value) ? value : undefined;
		case 'text': {
			if (typeof value !== 'string') return undefined;
			const trimmed = value.trim();
			return trimmed ? trimmed.slice(0, TEXT_MAX) : undefined;
		}
		case 'longText':
			return typeof value === 'string' && value ? value.slice(0, LONG_TEXT_MAX) : undefined;
		case 'orderType':
			return value === 'order' || value === 'opportunity' ? value : undefined;
	}
};

export type SanitizedPayload = {
	/** Set only when the Page asked to open an existing record. */
	id?: number;
	/** Declared prefill fields that passed validation. */
	prefill: Record<string, string | number>;
	/** Keys the Page sent that this entity doesn't declare — a page bug, reported not ignored. */
	unknownKeys: string[];
	/** Declared keys whose value failed validation — also a page bug. */
	invalidKeys: string[];
};

/**
 * Reduce a raw postMessage payload to the fields `entity` actually declares.
 * Never throws: the caller decides what to do with `unknownKeys`/`invalidKeys`
 * (the host reports them and carries on with what was valid, so a single bad
 * field doesn't cost the user the whole action).
 */
export function sanitizePageEntityPayload(entity: PageEntity, raw: unknown): SanitizedPayload {
	const spec = PAGE_ENTITY_SPECS[entity];
	const result: SanitizedPayload = { prefill: {}, unknownKeys: [], invalidKeys: [] };
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		return result;
	}

	for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
		if (value === undefined || value === null) continue;

		if (key === 'id') {
			if (!spec.open) {
				result.unknownKeys.push(key);
			} else if (isPositiveInt(value)) {
				result.id = value;
			} else {
				result.invalidKeys.push(key);
			}
			continue;
		}

		const type = spec.fields[key];
		if (!type) {
			result.unknownKeys.push(key);
			continue;
		}
		const cleaned = cleanValue(type, value);
		if (cleaned === undefined) {
			result.invalidKeys.push(key);
		} else {
			result.prefill[key] = cleaned;
		}
	}

	return result;
}
